import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import NurtureBarStep from './NurtureBarStep/index';
import NurtureBarSummary from './NurtureBarSummary/index';
import styles from './styles.module.css';
import appStyles from '../App/styles.module.css';
import NURTURE_STEPS, { INTEGRATIONS_MODAL_WITH_SERVICES, CONFIRM_EXIT_MODAL } from './nurture-content';
import { getTraits } from '../../redux/persona/selectors';
import { fetchServicesData } from '../../redux/services/actions';
import {
  accountHasAnIntegration,
  getServiceWithoutIntegration,
} from '../../redux/services/selectors';
import {
  trackEvent,
  CLICKED_NURTURE_BANNER,
  NURTURE_BAR_POPUP,
  NURTURE_BAR_TYPES,
  CLICKED_NURTURE_BAR_POPUP,
} from '../../util/segment';
import {
  getDaysLeftOnTrial,
  isActiveAccountState,
  getUsersCount,
  getUserId,
  getContactMethodsCount,
  isUserAResponderOrManager,
  shouldBlockRespondersAndActives,
} from '../../redux/fe-data/selectors';
import NurtureModal from './NurtureModal';

class NurtureBar extends Component {
  constructor(props) {
    super(props);
    this.state = {
      steps: [],
      stepIndex: 0,
      showSummary: true,
      isNurtureBarExited: false,
      loaded: false,
    };
  }

  componentDidMount = async () => {
    await this.props.fetchServicesData();

    let steps = [];

    // The goals we show are dependant on the account's state and the user's role
    if (this.props.showRespondersNurtureBar) {
      steps = this.getStepsForResponders();
    } else if (this.props.isActiveAccount) {
      steps = this.getStepsForActiveOwnerAdmins();
    } else {
      steps = this.getStepsForTrialOwnerAdmins();
    }

    // Loop through steps and mark steps that are completed
    steps = steps.map(this.markCompletedSteps);

    // Find first incomplete step
    const stepIndex = this.findFirstIncompleteStep(steps);

    // Place into state the modal id that is intended to be opned if any
    const showModalWithId = window.sessionStorage.getItem('showModalWithId');
    window.sessionStorage.removeItem('showModalWithId');

    // Which nurture bar are we showing
    const nurtureBarType = this.props.showRespondersNurtureBar
      ? NURTURE_BAR_TYPES.responder : NURTURE_BAR_TYPES.accountOwner;

    this.determineNurtureExitStatus();

    // Set the state
    this.setState({
      steps,
      stepIndex,
      showModalWithId,
      nurtureBarType,
      loaded: true,
    });
  }

  determineNurtureExitStatus = () => {
    const isNurtureBarExited = this.getIsNurtureExited();
    const initialLoadDate = parseInt(this.getInitialLoadDate(), 10);

    if (isNurtureBarExited) {
      // If the nurture bar is already exited set this in state.
      this.setState({ isNurtureBarExited });
    } else if (!initialLoadDate) {
      // The nurture bar hasn't been loaded in this browser before, set the initial load date.
      this.setInitialLoadDate();
    } else if (this.isTwoWeeksLater(initialLoadDate)) {
      // Two weeks after the initial load date, the nurture bar should exit.
      this.exitNurtureBar(false);
    }
  }

  isTwoWeeksLater = (initialLoadDate) => {
    const twoWeeksInMilliseconds = 14 * 24 * 60 * 60 * 1000;
    return Date.now() >= (initialLoadDate + twoWeeksInMilliseconds);
  }

  exitNurtureBar = (exitedManually) => {
    if (exitedManually) {
      trackEvent(CLICKED_NURTURE_BAR_POPUP, {
        nurtureBarType: this.state.nurtureBarType,
        notificationType: '',
        message: CONFIRM_EXIT_MODAL.confirmCaption,
        ctaType: 'exit_nurture_bar',
      });
    }
    this.unsetShowModalWithId();
    this.setIsNurtureExited();
    this.removeInitialLoadDate();
    this.setState({ isNurtureBarExited: true });
  }

  setInitialLoadDate = () => window.localStorage.setItem('initialLoadDate', Date.now().toString());

  getInitialLoadDate = () => window.localStorage.getItem('initialLoadDate');

  removeInitialLoadDate = () => window.localStorage.removeItem('initialLoadDate');

  setIsNurtureExited = () => window.localStorage.setItem('isNurtureExited', 'true');

  getIsNurtureExited = () => window.localStorage.getItem('isNurtureExited');

  getStepsForTrialOwnerAdmins = () => [
    this.getConfiguredIntegrationStep(),
    NURTURE_STEPS.downloadApp,
    NURTURE_STEPS.addUsers
  ];

  getStepsForResponders =() => [
    this.setStepUrlToUserProfile(NURTURE_STEPS.contactMethods),
    NURTURE_STEPS.downloadApp,
    this.setStepUrlToUserProfile(NURTURE_STEPS.manageOnCall),
    NURTURE_STEPS.ackAndOwnIncidents,
  ];

  getStepsForActiveOwnerAdmins = () => [NURTURE_STEPS.downloadApp, NURTURE_STEPS.addUsers];

  // The integration goal requires us to link directly into an existing service's
  // integration page, provided it does not have an integration setup
  getConfiguredIntegrationStep = () => (
    this.props.serviceWithoutIntegration.id ? {
      ...NURTURE_STEPS.integration,
      url: `/services/${this.props.serviceWithoutIntegration.id}/integrations`,
      modal: INTEGRATIONS_MODAL_WITH_SERVICES,
    } : NURTURE_STEPS.integration
  )

  setStepUrlToUserProfile = step => ({
    ...step,
    url: `/users/${this.props.userId}`,
  })

  // Returns an array of steps with their complete state set true/false based
  // on the users persona profile.
  markCompletedSteps = (step) => {
    let completed = false;

    const stepCompletenessCriteria = {
      integration: this.props.isIntegrationsStepComplete,
      addUsers: this.props.usersCount > 1,
      contactMethods: this.props.contactMethodsCount > 1,
    };

    if (this.props.traits[step.audienceKey] === true) {
      completed = true;
    } else if (step.id in stepCompletenessCriteria) {
      completed = stepCompletenessCriteria[step.id];
    }

    return {
      ...step,
      completed
    };
  };

  getCurrentStep = () => {
    const index = this.state.stepIndex;
    return this.state.steps[index];
  };

  getStepTrackData = step => (
    {
      stepTotal: this.state.steps.length,
      // steps are zero indexed
      stepNumber: step ? this.state.steps.findIndex(s => s.id === step.id) + 1 : null,
      stepCompleted: step ? step.completed : null,
      message: step ? step.text : null,
      nurtureBarType: this.state.nurtureBarType,
      notificationType: '',
    }
  );

  goToNextStep = async () => {
    await trackEvent(CLICKED_NURTURE_BANNER, {
      ctaType: 'Down Arrow',
      ...this.getStepTrackData(this.getCurrentStep())
    });

    // increment step
    const stepIndex = this.state.stepIndex + 1;
    if (stepIndex < this.state.steps.length) {
      this.setState({ stepIndex });
    }
  };

  goToPreviousStep = async () => {
    await trackEvent(CLICKED_NURTURE_BANNER, {
      ctaType: 'Up Arrow',
      ...this.getStepTrackData(this.getCurrentStep())
    });

    // decrement step
    const stepIndex = this.state.stepIndex - 1;
    if (stepIndex >= 0) {
      this.setState({ stepIndex });
    }
  };

  viewStep = async (cta) => {
    const currentStep = this.getCurrentStep();

    // wait to ensure track is fired before possible redirect
    await trackEvent(CLICKED_NURTURE_BANNER, {
      ctaType: cta,
      ...this.getStepTrackData(this.getCurrentStep())
    });

    // If showing the download app modal, we do not need to redirect to a specific page.
    if (!currentStep.url) {
      this.setState({
        showModalWithId: currentStep.id,
        showSummary: true,
      });
    } else {
      // Remember in sessionStorage what modal to display
      window.sessionStorage.setItem('showModalWithId', currentStep.id);
      window.location.href = currentStep.url;
    }
  };

  findFirstIncompleteStep = (steps) => {
    const index = steps.findIndex(item => !item.completed);
    return (index > 0) ? index : 0;
  };

  toggleSummary = (summaryText) => {
    trackEvent(CLICKED_NURTURE_BANNER, {
      ...this.getStepTrackData(null),
      ctaType: 'Step Summary',
      message: summaryText,
    });
    const { showSummary, steps } = this.state;
    const stepIndex = this.findFirstIncompleteStep(steps);
    this.setState({
      stepIndex,
      showSummary: !showSummary
    });
  };

  closeStep = () => {
    trackEvent(CLICKED_NURTURE_BANNER, {
      ctaType: 'Close Step',
      ...this.getStepTrackData(this.getCurrentStep())
    });
    this.setState({
      showSummary: true
    });
  };

  countCompletedSteps = () => this.state.steps.filter(step => step.completed).length;

  getModalToOpen = () => {
    const { showModalWithId, steps } = this.state;
    let modal = null;
    let modalIndex = 0;

    if (showModalWithId === CONFIRM_EXIT_MODAL.id) {
      modal = CONFIRM_EXIT_MODAL;
      modalIndex = -1;
      trackEvent(NURTURE_BAR_POPUP, {
        ...this.getStepTrackData(null),
        ctaType: null,
        message: modal.title,
      });
      return { modalIndex, modal };
    }

    if (showModalWithId) {
      modalIndex = steps.findIndex(item => item.id === showModalWithId) + 1;
      const step = NURTURE_STEPS[showModalWithId];
      modal = step.modal || null;
      if (modal) {
        trackEvent(NURTURE_BAR_POPUP, {
          ...this.getStepTrackData(step),
          ctaType: null,
          message: step.modal.title,
        });
      }
    }

    return { modalIndex, modal };
  };

  showConfirmExitModal = () => {
    trackEvent(CLICKED_NURTURE_BANNER, {
      ...this.getStepTrackData(null),
      ctaType: 'Exit',
      message: 'x',
    });
    this.setState({ showModalWithId: CONFIRM_EXIT_MODAL.id });
  }

  unsetShowModalWithId = () => { this.setState({ showModalWithId: null }); };

  render() {
    const { modalIndex, modal } = this.getModalToOpen();

    if (this.props.shouldBlockRespondersAndActives) return null;

    return this.state.isNurtureBarExited || !this.state.loaded
      ? null
      : (
        <div className={appStyles.reactNurtureBar}>
          <div className={styles.nurtureBar}>
            {
              this.state.showSummary
                ? (
                  <NurtureBarSummary
                    onSummaryClick={this.toggleSummary}
                    totalCompletedSteps={this.countCompletedSteps()}
                    totalSteps={this.state.steps.length}
                    daysLeft={this.props.daysLeft}
                    showConfirmExitModal={this.showConfirmExitModal}
                  />
                )
                : (
                  <NurtureBarStep
                    index={this.state.stepIndex}
                    totalSteps={this.state.steps.length}
                    step={this.getCurrentStep()}
                    goToPreviousStep={this.goToPreviousStep}
                    goToNextStep={this.goToNextStep}
                    viewStep={this.viewStep}
                    closeStep={this.closeStep}
                  />
                )
            }
            {
              this.state.showModalWithId
              && (
                <NurtureModal
                  index={modalIndex}
                  namespace={modal.namespace}
                  width={modal.width}
                  title={modal.title}
                  body={modal.body()}
                  btnCaption={modal.btnCaption}
                  unsetShowModalWithId={this.unsetShowModalWithId}
                  exitNurtureBar={this.exitNurtureBar}
                  nurtureBarType={this.state.nurtureBarType}
                  customFooter={modal.customFooter}
                />
              )
            }
          </div>
        </div>
      );
  }
}

NurtureBar.defaultProps = {
  daysLeft: null,
};

NurtureBar.propTypes = {
  fetchServicesData: PropTypes.func.isRequired,
  traits: PropTypes.shape({}).isRequired,
  daysLeft: PropTypes.number,
  usersCount: PropTypes.number.isRequired,
  isIntegrationsStepComplete: PropTypes.bool.isRequired,
  serviceWithoutIntegration: PropTypes.shape({
    id: PropTypes.string,
  }).isRequired,
  showRespondersNurtureBar: PropTypes.bool.isRequired,
  userId: PropTypes.string.isRequired,
  contactMethodsCount: PropTypes.number.isRequired,
  isActiveAccount: PropTypes.bool.isRequired,
  shouldBlockRespondersAndActives: PropTypes.bool.isRequired,
};

const mapStateToProps = state => ({
  traits: getTraits(state),
  daysLeft: getDaysLeftOnTrial(state),
  isActiveAccount: isActiveAccountState(state),
  usersCount: getUsersCount(state),
  isIntegrationsStepComplete: accountHasAnIntegration(state),
  serviceWithoutIntegration: getServiceWithoutIntegration(state),
  userId: getUserId(state),
  contactMethodsCount: getContactMethodsCount(state),
  showRespondersNurtureBar: isUserAResponderOrManager(state),
  shouldBlockRespondersAndActives: shouldBlockRespondersAndActives(state),
});

const mapActionsToProps = {
  fetchServicesData,
};
export default connect(mapStateToProps, mapActionsToProps)(NurtureBar);
